import type { OpenStatus, ShopInfo } from '../../lib/twin';
import { CtaButton } from './CtaButton';
import { OpenStatusBadge } from './OpenStatusBadge';

interface Props {
  shop: ShopInfo;
  status: OpenStatus;
  onOpenBot: () => void;
}

/**
 * Hero — brand, tagline, live open/closed badge, and the two primary CTAs.
 * Holds the single <h1> for the page. CTAs render above the fold on mobile.
 */
export function Hero({ shop, status, onOpenBot }: Props) {
  return (
    <section
      id="top"
      aria-labelledby="hero-heading"
      className="relative overflow-hidden bg-espresso text-cream-50"
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-espresso via-espresso-soft to-mocha" />
      <div className="relative mx-auto flex max-w-6xl flex-col gap-10 px-5 py-14 sm:py-20 lg:flex-row lg:items-center lg:gap-12 lg:py-24">
        <div className="flex-1">
          <p className="mb-4 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-foam">
            <span aria-hidden="true">☕</span> {shop.neighborhood}
          </p>

          <h1
            id="hero-heading"
            className="text-balance text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl"
          >
            {shop.name}
          </h1>

          <p className="mt-4 max-w-xl text-balance text-lg text-cream-100 sm:text-xl">
            {shop.tagline}
          </p>

          <div className="mt-6">
            <OpenStatusBadge status={status} />
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <CtaButton href="#menu" variant="primary" size="lg">
              Order Now
            </CtaButton>
            <CtaButton
              onClick={onOpenBot}
              variant="secondary"
              size="lg"
              ariaLabel="Ask our barista bot — opens a chat panel"
            >
              Ask our barista bot
            </CtaButton>
          </div>

          <p className="mt-4 text-sm text-foam">
            No account needed · Ready for pickup in minutes
          </p>
        </div>

        {/* Hero image placeholder — decorative, so alt is empty. */}
        <div className="flex-1" aria-hidden="true">
          <div className="relative mx-auto aspect-[4/3] w-full max-w-md rounded-[2rem] bg-gradient-to-tr from-mocha to-latte shadow-2xl shadow-black/30 ring-1 ring-white/10">
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[8rem] leading-none drop-shadow-lg sm:text-[10rem]">
                ☕
              </span>
            </div>
            <span className="absolute bottom-4 right-5 rounded-full bg-espresso/70 px-3 py-1 text-xs font-medium text-cream-100">
              hero image placeholder
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
